import { useState, useEffect } from 'react';
import { useFrappeGetCall } from 'frappe-react-sdk';
import { SessionData } from '../types/session.types';
import toast from 'react-hot-toast';

interface UseSessionDataProps {
  sessionName: string | null;
  authUser: string | null;
}

export const useSessionData = ({ sessionName, authUser }: UseSessionDataProps) => {
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [isAccessPending, setIsAccessPending] = useState<boolean>(true);

  // Check user permissions for this session
  const isAdmin = authUser === 'administrator@gmail.com';

  useEffect(() => {
    const checkAccess = async () => {
      if (!sessionName || !authUser) {
        setIsAccessPending(false);
        return;
      }

      // Admin always has access - grant immediately
      if (isAdmin) {
        setHasAccess(true);
        setIsAccessPending(false);
        return;
      }

      // For non-admin users, grant access by default and let the API handle access control
      setHasAccess(true);
      setIsAccessPending(false);
    };

    checkAccess();
  }, [sessionName, authUser, isAdmin]);

  // Always call the hook - don't make it conditional
  // Note: useFrappeGetCall returns the API response directly, not wrapped in another object
  const { data, error: fetchError, isLoading, mutate } = useFrappeGetCall(
    'surgical_training.api.session.get_session_details',
    sessionName ? { session_name: sessionName } : undefined,
    sessionName ? `session-${sessionName}` : null,
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000,
      errorRetryCount: 1,
    }
  );
  

  useEffect(() => {
    // Handle successful data - the API returns {"message": "Success", "data": {...}}
    if (data) {
      // The useFrappeGetCall wraps the API response: {message: {message: "Success", data: sessionData}}  
      if ((data as any).message?.message === 'Success' && (data as any).message?.data) {
        setSessionData((data as any).message.data);
        setError(null);
      } else if ((data as any).message === 'Success' && (data as any).data) {
        // Fallback: Direct API response format (less likely but possible)
        setSessionData((data as any).data);
        setError(null);
      } else if ((data as any).videos) {
        // Fallback: If data is returned directly (less likely)
        setSessionData(data as unknown as SessionData);
        setError(null);
      }
    } else if (fetchError) {
      
      if (typeof fetchError === 'object' && fetchError !== null) {
        if ('_server_messages' in fetchError) {
          try {
            const messages = JSON.parse((fetchError as any)._server_messages);
            if (Array.isArray(messages) && messages.length > 0) {
              const msg = messages[0];
              if (typeof msg === 'string') {
                const parsedMsg = JSON.parse(msg);
                if (parsedMsg?.message) {
                  setError(parsedMsg.message);
                } else {
                  setError('Session not found or access denied');
                }
              }
            }
          } catch (parseError) {
            setError('Session not found or access denied');
          }
        } else if ('message' in fetchError) {
          setError((fetchError as any).message);
        } else {
          setError('Failed to load session data');
        }
      } else {
        setError('Failed to load session data');
      }
    }
  }, [data, fetchError]);

  const refreshSession = async () => {
    try {
      await mutate();
      toast.success('Session data refreshed');
    } catch (error) {
      // Error refreshing session
      toast.error('Failed to refresh session data');
    }
  };


  return {
    sessionData,
    error,
    hasAccess,
    isAccessPending,
    isLoading,
    refreshSession,
    isAdmin
  };
};